import { makeObservable, observable, computed, action } from "mobx";
import { ContentSource } from "../../lib/ContentSource";
import { DbInferrerOfSearchTermsFromDomEvents } from "./InferrerOfSearchTermsFromDomEvents";
import { AllOption, SingleOriginFilterState, urlFilterFor } from "./SingleOriginFilterState";
import { ExtensionSettingsAccessor } from "../../State/EvidenceDB/DbExtensionSettings";
import { DbRecordingEntryWriter } from "../../State/EvidenceDB/DbRecordingEntryWriter";
import { GlobalContext } from "./GlobalContext";
import { OriginAndDestinationUrlsObserver } from "../../lib/Analysis/OriginAndDestinationUrlsObserver";
import { UUID } from "../../State/EvidenceDB/DatabaseTypes";

export interface RecordingSearchStateInheritedState extends GlobalContext {
	readonly getDomainOrUrlToFilterFor: () => string;
	readonly getSourceDestinationUrlStats: () => OriginAndDestinationUrlsObserver
}

export class SearchUiState  {
	readonly destinationUrlFilterState: SingleOriginFilterState;
	readonly inferrerOfSearchTermsFromDomEvents: DbInferrerOfSearchTermsFromDomEvents;

	_searchTermTextAreaValue: string | undefined;
	_showOnlySearchTerm: string | undefined;
	_dataFlowDirection: AllOption | ContentSource["direction"] = 'sent';

	get dbRecordingEntryWriter() { return DbRecordingEntryWriter.fromRecordingUuid(this.recordingUuid); }
	// get networkTrafficRecordingState() { return this.inheritedState.networkTrafficRecordingState; }
	get domainOrUrlToFilterFor() { return this.inheritedState.getDomainOrUrlToFilterFor() }

	@computed get sourcePageUrlFilter() {
		return urlFilterFor(this.domainOrUrlToFilterFor)
	}

	@computed get sourceDestinationUrlStats() {
		return this.inheritedState.getSourceDestinationUrlStats();
	};


	getDestinationNetworkStatisticsTree = (thirdPartyDestinationsOnly: boolean) =>
		this.sourceDestinationUrlStats.destinationNetworkOriginStatisticsTreeFilteredBySourcePage(
			this.sourcePageUrlFilter, thirdPartyDestinationsOnly);
	
	@computed get thirdPartyDestinationStatisticsTree() {
		return this.getDestinationNetworkStatisticsTree(true);
	}

	constructor(public readonly recordingUuid: UUID, protected readonly inheritedState: RecordingSearchStateInheritedState) {
		this.destinationUrlFilterState = new SingleOriginFilterState(
			this.getDestinationNetworkStatisticsTree, () => this.inheritedState.getDomainOrUrlToFilterFor()
		);
		this.inferrerOfSearchTermsFromDomEvents = new DbInferrerOfSearchTermsFromDomEvents(this.inheritedState.db, recordingUuid);
		makeObservable(this, {
			//			_tabIdRecordedToUrlsRecorded: observable,
			_searchTermTextAreaValue: observable,
			_showOnlySearchTerm: observable,
			_dataFlowDirection: observable,
		});
	}

	@computed get recording() { return this.dbRecordingEntryWriter?.recording; }

	@computed get searchTermTextAreaValue() {
		return this._searchTermTextAreaValue ??
			this.dbRecordingEntryWriter?.recording?.searchTermsNewlineSeparated ?? "";
	}

	setSearchTermTextAreaValueWithoutUpdatingDatabase = action ((value: string) => {
		this._searchTermTextAreaValue = value;
	});

	setSearchTermTextAreaValue = async (value: string) => {
		this.setSearchTermTextAreaValueWithoutUpdatingDatabase(value);
		await this.dbRecordingEntryWriter?.updateRecording({
			searchTermsNewlineSeparated: value
		});
	}

	@computed get GlobalSearchTermTextAreaValue() {
		if (window.location.protocol.startsWith("file")) {
			return this.recording?.globalSearchTermsNewlineSeparated ?? "";
		} else {
			return ExtensionSettingsAccessor.current?.sharedSearchTermField ?? "";
		}
	}

	@computed get GlobalSearchTermArray() {
		return this.GlobalSearchTermTextAreaValue.split("\n").map( s => s.trim() ).filter( s => s.length > 0);
	}

	setGlobalSearchTermAreaValue = async (value: string) => {
		await Promise.all([
			ExtensionSettingsAccessor.updateSetting("sharedSearchTerms", value),
			this.dbRecordingEntryWriter?.updateRecording({
				globalSearchTermsNewlineSeparated: value
			}),	
		])
	};

	@computed get searchTermsFromTextArea(): string[] {
		if (this._searchTermTextAreaValue == null) {
			return (this.recording?.searchTermsNewlineSeparated ?? "").split("\n")
			.map(s => s.trim())
			.filter(s => s.length > 0).sort();
		} else {
			return this._searchTermTextAreaValue.split("\n")
				.map(s => (s.split(" # ")[0] ?? "").trim())
				.filter((line) => line.length >= 3);
		}
	}

	@computed get searchTerms(): string[] {
		const result = [...new Set<string>([
			...this.searchTermsFromDomEvents.filter( ([, searchByDefault]) => searchByDefault).map( ([searchTerm]) => searchTerm),
			...this.searchTermsFromTextArea,
			...this.GlobalSearchTermArray,
		]
		).values()]
			.map(s => s.trim())
			.filter(s => s.length >= 3 && !s.startsWith('# '))
			.sort();
		// ProcessInfo.log("searchTerms", toJS(result));
		return result;
	}


	@computed get searchTermsFromDomEvents(): [string, boolean][] {
		return this.inferrerOfSearchTermsFromDomEvents?.searchTermsFromDomEventsSortedAlphanumeric ?? [];
	}

	@computed get showOnlySearchTerm() {
		return (
			this._showOnlySearchTerm == null ||
			!this.searchTerms.includes(this._showOnlySearchTerm)
		) ?
			undefined :
			this._showOnlySearchTerm;
	}
	setShowOnlySearchTerm = action((searchTerm: string | undefined) => this._showOnlySearchTerm = searchTerm);
	get dataFlowDirection() { return this._dataFlowDirection; }
	setDataFlowDirection = action((direction: string) => {
		this._dataFlowDirection = direction === "sent" || direction === "received" ? direction : AllOption;
	});

}


